/**
 * ReportStatsBar Component
 *
 * Compact horizontal stats bar for the unified reports page.
 * Shows key metrics in a sleek, modern design.
 */

import React from 'react'
import { Users, Target, CheckCircle, Clock, BarChart3 } from 'lucide-react'

interface ReportStats {
  totalSessions: number
  totalAnalyses: number
  totalDetections: number
  totalUserDrawn: number
  totalCorrected: number
  avgProcessingTime: number
  confidenceStats: {
    certain: number
    uncertain: number
  }
}

interface ReportStatsBarProps {
  stats: ReportStats | null
  loading: boolean
  selectedDate: Date | null
  selectedRoomsCount: number
}

const StatCard: React.FC<{
  icon: React.ReactNode
  label: string
  value: string | number
  subValue?: string
  color: string
  loading: boolean
}> = ({ icon, label, value, subValue, color, loading }) => (
  <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 w-1/2 sm:w-1/3 md:w-auto md:flex-1 border-b md:border-b-0 border-gray-100">
    <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
        {loading ? (
          <div className="h-5 w-10 sm:w-12 bg-gray-200 rounded animate-pulse" />
        ) : (
          value
        )}
      </div>
      <div className="text-[10px] sm:text-xs text-gray-500 truncate">{label}</div>
      {subValue && !loading && (
        <div className="text-[9px] sm:text-[10px] text-gray-400 truncate">{subValue}</div>
      )}
    </div>
  </div>
)

const ReportStatsBar: React.FC<ReportStatsBarProps> = ({
  stats,
  loading,
  selectedDate,
  selectedRoomsCount,
}) => {
  if (!selectedDate || selectedRoomsCount === 0) {
    return null
  }

  const accuracyScore = stats && stats.totalDetections > 0
    ? Math.round((stats.confidenceStats.certain / stats.totalDetections) * 100)
    : 100

  const userInterventionRate = stats && stats.totalDetections > 0
    ? Math.round(((stats.totalCorrected + stats.totalUserDrawn) / stats.totalDetections) * 100)
    : 0

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="flex flex-wrap md:flex-nowrap items-stretch md:divide-x divide-gray-100">
        {/* Sessions */}
        <StatCard
          icon={<Users className="h-4 w-4 text-purple-600" />}
          label="Sessions"
          value={stats?.totalSessions || 0}
          subValue={stats ? `${stats.totalAnalyses} analyses` : undefined}
          color="bg-purple-50"
          loading={loading}
        />

        {/* Detections */}
        <StatCard
          icon={<Target className="h-4 w-4 text-green-600" />}
          label="Detections"
          value={stats?.totalDetections || 0}
          subValue={stats ? `${stats.confidenceStats.certain} confident` : undefined}
          color="bg-green-50"
          loading={loading}
        />

        {/* User Actions */}
        <StatCard
          icon={<CheckCircle className="h-4 w-4 text-blue-600" />}
          label="User Actions"
          value={(stats?.totalCorrected || 0) + (stats?.totalUserDrawn || 0)}
          subValue={stats ? `${stats.totalCorrected} corrected, ${stats.totalUserDrawn} drawn` : undefined}
          color="bg-blue-50"
          loading={loading}
        />

        {/* Confidence */}
        <StatCard
          icon={<BarChart3 className="h-4 w-4 text-teal-600" />}
          label="Accuracy"
          value={`${accuracyScore}%`}
          subValue={`${userInterventionRate}% intervention`}
          color="bg-teal-50"
          loading={loading}
        />

        {/* Avg Time */}
        <StatCard
          icon={<Clock className="h-4 w-4 text-amber-600" />}
          label="Avg Time"
          value={stats?.avgProcessingTime ? `${stats.avgProcessingTime.toFixed(1)}s` : '0s'}
          subValue={selectedDate.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          color="bg-amber-50"
          loading={loading}
        />
      </div>
    </div>
  )
}

export default ReportStatsBar
